// Atualiza o titulo da aba do navegador conforme a rota. Formato: "Pagina - FIODECOBRE".
//
// Estrategia: o titulo da aba e o texto DIRETO do primeiro `.pagina-titulo` renderizado
// (badge, contador e botao voltar sao filhos do titulo e ficam de fora — ver `obterTituloDaPagina`).
// Cobre todas as telas que seguem o padrao do design system sem precisar de mapeamento
// rota-a-rota. Como o `.pagina-titulo` pode aparecer apos fetch async, re-checa em alguns
// intervalos curtos pos-navegacao.
//
// Fallback: mapeamento ROTAS abaixo cobre paginas sem `.pagina-titulo` ou que carregam tarde.
// AtendimentoHome controla o proprio titulo (contador de nao-lidas) — esse hook ignora /atendimento.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const APP = 'FIODECOBRE';

// Ordem importa — rotas mais especificas vem primeiro.
const ROTAS: Array<[RegExp, string]> = [
  [/^\/cadastros\/usuarios/, 'Usuarios'],
  [/^\/cadastros\/clientes/, 'Clientes'],
  [/^\/cadastros\/fornecedores/, 'Fornecedores'],
  [/^\/cadastros\/grupos/, 'Grupos'],
  [/^\/cadastros\/categorias/, 'Categorias'],
  [/^\/cadastros\/marcas/, 'Marcas'],
  [/^\/cadastros\/unidades-medida/, 'Unidades de Medida'],
  [/^\/configuracoes\/situacoes-tributarias/, 'Situacoes Tributarias'],
  [/^\/configuracoes\/cores/, 'Cores'],
  [/^\/configuracoes\/fiscal/, 'Configuracao Fiscal'],
  [/^\/configuracoes\/recebimentos/, 'Recebimentos'],
  [/^\/configuracoes\/dispositivos/, 'Dispositivos Autorizados'],
  [/^\/produtos\/(novo|\d+)/, 'Produto'],
  [/^\/produtos/, 'Produtos'],
  [/^\/orcamentos\/novo/, 'Novo Orcamento'],
  [/^\/orcamentos\/\d+/, 'Orcamento'],
  [/^\/orcamentos/, 'Orcamentos'],
  [/^\/cotacoes\/nova/, 'Nova Cotacao'],
  [/^\/cotacoes\/mais-vendidos/, 'Mais Vendidos'],
  [/^\/cotacoes\/\d+\/comparativo/, 'Comparativo de Cotacao'],
  [/^\/cotacoes\/\d+\/respostas/, 'Cotacao'],
  [/^\/cotacoes/, 'Cotacoes'],
  [/^\/catalogo-fornecedor/, 'Catalogo de Fornecedor'],
  [/^\/catalogo-gestao/, 'Catalogo Digital'],
  [/^\/promocoes/, 'Promocoes'],
  [/^\/lista-compras/, 'Lista de Compras'],
  [/^\/notas-entrada\/importar/, 'Importar Nota de Entrada'],
  [/^\/notas-entrada\/\d+\/revisar-margens/, 'Revisar Margens'],
  [/^\/notas-entrada/, 'Notas de Entrada'],
  [/^\/contas-receber/, 'Contas a Receber'],
  [/^\/contas-pagar/, 'Contas a Pagar'],
  [/^\/notas-fiscais/, 'Notas Fiscais'],
  [/^\/caixa/, 'Caixa'],
  [/^\/trocas/, 'Trocas e Devolucoes'],
  [/^\/ponto/, 'Ponto'],
  [/^\/equipe\/estatisticas-agentes/, 'Estatisticas dos Agentes'],
  [/^\/equipe/, 'Equipe'],
  [/^\/comissoes\/configurar/, 'Configurar Comissoes'],
  [/^\/comissoes/, 'Comissoes'],
  [/^\/manual/, 'Manual'],
  [/^\/treinamento/, 'Videos de Treinamento'],
  [/^\/$/, 'Inicio'],
];

export function tituloDaRota(pathname: string): string | null {
  for (const [re, label] of ROTAS) {
    if (re.test(pathname)) return label;
  }
  return null;
}

// Rotas em que o `.pagina-titulo` e CONTEUDO, nao o nome da tela — nelas o mapa ROTAS manda.
// /manual: o titulo padronizado da area de leitura e o capitulo aberto, entao a aba dizia
// "Orcamentos - FIODECOBRE" pra quem estava no Manual lendo aquele capitulo.
// /treinamento: idem — o titulo da pagina e o video aberto.
const ROTAS_QUE_IGNORAM_O_DOM = [/^\/manual/, /^\/treinamento/];

function obterTituloDaPagina(): string | null {
  const el = document.querySelector('.pagina-titulo');
  if (!el) return null;
  // So os nos de TEXTO diretos: badges, contadores e o botao voltar sao filhos do titulo
  // (ver `sistema/CLAUDE.md` § UI) e vazariam pra aba grudados na palavra — "Comissoes" com o
  // selo de modo virava `ComissoesPela Loja`, e "Produtos" carregava o `(446)` do contador.
  const direto = [...el.childNodes]
    .filter((n) => n.nodeType === Node.TEXT_NODE)
    .map((n) => n.textContent ?? '')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return direto || el.textContent?.trim() || null;
}

export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/atendimento')) return;
    const soPelaRota = ROTAS_QUE_IGNORAM_O_DOM.some((re) => re.test(pathname));
    function atualizar() {
      const label = (soPelaRota ? null : obterTituloDaPagina()) ?? tituloDaRota(pathname);
      document.title = label ? `${label} - ${APP}` : APP;
    }
    atualizar();
    // Re-checagens pra cobrir lazy load / fetch que renderiza titulo apos a navegacao.
    const tims = [60, 200, 500, 1500].map((d) => window.setTimeout(atualizar, d));
    return () => tims.forEach((t) => window.clearTimeout(t));
  }, [pathname]);
}
